import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errMsg, fmtPrice, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { CourseThumb, Stars, fmtCount } from "../lib/art";
import { toast } from "sonner";

const PlaySvg = ({ s = 20 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="#04121f"><path d="M8 5v14l11-7z" /></svg>;

function ratingDist(reviews) {
  const counts = [0, 0, 0, 0, 0];
  (reviews || []).forEach((r) => {
    const n = Math.min(5, Math.max(1, Math.round(r.rating)));
    counts[5 - n] += 1;
  });
  const total = (reviews || []).length;
  return counts.map((c) => (total ? Math.round((c / total) * 100) : 0));
}

const REV_COLORS = [["var(--bp-dim)", "var(--bp)"], ["var(--land-dim)", "var(--land)"], ["rgba(255,120,71,.14)", "var(--ember)"], ["rgba(245,192,78,.14)", "var(--gold)"]];

const pad = (n) => String(n).padStart(2, "0");

const SaleTimer = ({ ends }) => {
  const [left, setLeft] = useState(ends ? new Date(ends) - Date.now() : null);
  useEffect(() => {
    if (!ends) return;
    const t = setInterval(() => setLeft(new Date(ends) - Date.now()), 1000);
    return () => clearInterval(t);
  }, [ends]);
  if (!ends) return <div className="buy-timer">⏳ Sale ends soon at this price!</div>;
  if (left <= 0) return <div className="buy-timer">Sale ended — refresh for current price</div>;
  const d = Math.floor(left / 86400000), h = Math.floor(left / 3600000) % 24,
    m = Math.floor(left / 60000) % 60, s = Math.floor(left / 1000) % 60;
  return (
    <div className="buy-timer" data-testid="sale-countdown">
      ⏳ Sale ends in <b style={{ fontFamily: "var(--font-d)" }}>{d > 0 && `${d}d `}{pad(h)}:{pad(m)}:{pad(s)}</b>
    </div>
  );
};

export default function CoursePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { cart, bundleCart, toggleCart, toggleBundle, sections, currency } = useStore();
  const [c, setC] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState({ 0: true });
  const [modal, setModal] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");

  const load = () => {
    api.get(`/courses/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
    api.get(`/courses/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); window.scrollTo(0, 0); }, [id]);

  if (c === null) return <div className="wrap page-pad" style={{ color: "var(--muted)", padding: "120px 24px" }}>Loading…</div>;
  if (c === false) return <div className="wrap page-pad" style={{ color: "var(--muted)", padding: "120px 24px" }}>Course not found.</div>;

  const secMap = Object.fromEntries(sections.map((s) => [s.id, s]));
  const sec = secMap[c.section] || {};
  const len = { h: Math.floor(c.total_duration_min / 60), m: c.total_duration_min % 60 };
  const onSale = c.effective_price < c.price;
  const disc = onSale ? Math.round((1 - c.effective_price / c.price) * 100) : 0;
  const inCart = cart.includes(c.id);
  const dist = ratingDist(reviews);
  const allOpen = c.curriculum.every((_, i) => open[i]);

  const enrollFree = async () => {
    if (!user) return nav("/auth?next=" + encodeURIComponent(`/course/${id}`));
    try {
      await api.post("/checkout", { course_ids: [c.id], currency, payment_method: "free" });
      toast.success("Enrolled! Start learning now.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { course_id: c.id, rating, text });
      toast.success("Review submitted ✓");
      setText("");
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const openPreview = (title, url) => setModal({ title, url });

  return (
    <div>
      <div className="cp-hero"><div className="wrap"><div className="cp-grid">
        <div>
          <div className="crumb" onClick={() => nav(`/courses?section=${c.section}`)}>{sec.title || c.section} <span>›</span> <span>{c.level}</span></div>
          <h1 className="cp-title" data-testid="course-title">{c.title}</h1>
          <p className="cp-sub">{c.subtitle}</p>
          <div className="cp-meta">
            {c.bestseller && <span className="bs-badge" style={{ position: "static" }}>BESTSELLER</span>}
            <span className="rv">{(c.rating || 0).toFixed(1)}</span><Stars r={c.rating || 0} />
            <a onClick={() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" })}>({fmtCount(c.rating_count)} ratings)</a>
            <span>{fmtCount(c.students_count)} students</span>
          </div>
          <div className="cp-meta2">
            <span>Created by <Link to="/instructor" style={{ color: "var(--bp)" }} data-testid="instructor-link">{c.instructor}</Link></span>
            <span>🗓 Last updated {c.updated || "2026"}</span><span>🌐 {c.language}</span>
          </div>
        </div>
        <div />
      </div></div></div>

      <div className="wrap cp-body"><div className="cp-cols">
        <div className="cp-main">
          <div className="wyl"><h2>What you'll learn</h2>
            <div className="wyl-grid">{c.bullets?.map((b, i) => <div key={i}>{b}</div>)}</div></div>

          <div>
            <h2>Course content</h2>
            <div className="curr-top">
              <span className="info">{c.curriculum.length} sections · {c.lecture_count} lectures · {len.h}h {len.m}m total length</span>
              <button className="expand-all" data-testid="expand-all-btn" onClick={() => {
                const anyClosed = c.curriculum.some((_, i) => !open[i]);
                setOpen(Object.fromEntries(c.curriculum.map((_, i) => [i, anyClosed])));
              }}>{allOpen ? "Collapse all sections" : "Expand all sections"}</button>
            </div>
            <div className="curr" data-testid="curriculum">
              {c.curriculum.map((cs, i) => (
                <div className={`c-sec ${open[i] ? "open" : ""}`} key={cs.id}>
                  <button className="c-sec-head" data-testid={`curriculum-section-${cs.id}`} onClick={() => setOpen({ ...open, [i]: !open[i] })}>
                    <span className="pm">+</span> {cs.title}
                    <span className="cnt">{cs.lectures.length} lectures · {cs.lectures.reduce((n, l) => n + (l.duration_min || 0), 0)}min</span>
                  </button>
                  <div className="c-lects">
                    {cs.lectures.map((l) => (
                      <div className="lect" key={l.id}>
                        <span className="ic">{l.preview ? "▶" : "🔒"}</span><span className="lt">{l.title}</span>
                        {l.preview && l.video_url && <button className="pv" data-testid={`preview-btn-${l.id}`} onClick={() => openPreview(l.title, l.video_url)}>Preview</button>}
                        <span className="ld">{l.duration_sec ? `${Math.floor(l.duration_sec / 60)}:${String(l.duration_sec % 60).padStart(2, "0")}` : `${l.duration_min}min`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div><h2>Requirements</h2><ul className="reqs">{c.requirements?.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
          <div><h2>Description</h2><div className="desc-text">{c.description}</div></div>

          <div><h2>Instructor</h2>
            <div className="inst-card">
              <div className="inst-av">{(c.instructor || "I").slice(0, 1)}</div>
              <div className="inst-info"><b><Link to="/instructor" style={{ color: "inherit" }}>{c.instructor}</Link></b>
                <div className="role">Unreal Engine Educator & Filmmaker</div>
                <div className="inst-stats"><span>★ {(c.rating || 0).toFixed(1)} Instructor Rating</span><span>{fmtCount(c.rating_count)} Reviews</span><span>{fmtCount(c.students_count)} Students</span></div>
                <p>Creator of cinematic Unreal Engine content and structured, project-based learning. Every course is built the same way: clear pacing, real projects, and nothing skipped.</p>
                <Link to="/instructor" className="text-sm" style={{ color: "var(--bp)" }} data-testid="view-full-profile">View full profile →</Link>
              </div>
            </div></div>

          <div id="reviews"><h2>Student feedback</h2>
            <div className="rev-sum">
              <div className="rev-big"><b>{(c.rating || 0).toFixed(1)}</b><Stars r={c.rating || 0} /><span>Course Rating</span></div>
              <div className="rev-bars">{dist.map((p, i) => (
                <div className="rbar" key={5 - i}><Stars r={5 - i} /><div className="track"><div className="fill" style={{ width: p + "%" }} /></div><span>{p}%</span></div>
              ))}</div>
            </div>
            {c.enrolled && (
              <form onSubmit={submitReview} className="rev" data-testid="review-form" style={{ borderColor: "var(--bp)" }}>
                <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} data-testid={`rating-star-${n}`} onClick={() => setRating(n)}
                      style={{ background: "none", border: "none", fontSize: 22, color: n <= rating ? "var(--gold)" : "#3a4351" }}>★</button>
                  ))}
                </div>
                <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Share your experience…" className="input-dark" rows={2} />
                <button className="btn-primary" data-testid="review-submit" style={{ marginTop: 10 }}>Submit Review</button>
              </form>
            )}
            {reviews.map((r, i) => (
              <div className="rev" key={r.id} data-testid={`review-${r.id}`}>
                <div className="rev-h">
                  <div className="rev-av" style={{ background: REV_COLORS[i % 4][0], color: REV_COLORS[i % 4][1] }}>
                    {r.user_name.split(" ").map((w) => w[0]).join("")}
                  </div>
                  <div><b>{r.user_name}</b><Stars r={r.rating} className="stars" /> <span>· {new Date(r.created_at).toLocaleDateString()}</span></div>
                </div>
                {r.text && <p>{r.text}</p>}
              </div>
            ))}
            {reviews.length === 0 && <div className="empty">No reviews yet.</div>}
          </div>
        </div>

        <aside><div className="buy-card">
          <div className="buy-vid" data-testid="buy-vid" onClick={() => openPreview("Course Trailer", c.video || c.curriculum.flatMap((s) => s.lectures).find((l) => l.preview && l.video_url)?.video_url || "")}>
            <CourseThumb course={c} color={sec.color} />
            {c.video?.includes("/api/media/") && (
              <img src={mediaUrl(c.video.split("?")[0] + "/thumb")} alt="" data-testid="trailer-thumb"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            <div className="pl"><div className="play-btn" style={{ transform: "scale(1)" }}><PlaySvg /></div></div>
            <div className="lbl">PREVIEW THIS COURSE</div>
          </div>
          <div className="buy-body">
            {c.enrolled ? (
              <div className="flex items-center gap-2 mb-1" data-testid="course-owned-badge" style={{ color: "var(--land)" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" fill="rgba(82,210,115,.12)" stroke="var(--land)" />
                  <path d="M8 12.5l2.5 2.5L16 9.5" />
                </svg>
                <span className="font-display font-bold" style={{ fontSize: 18, letterSpacing: 1 }}>YOU OWN THIS COURSE</span>
              </div>
            ) : (
              <>
                <div className="buy-price">
                  <span className={`now ${c.effective_price === 0 ? "free" : ""}`} data-testid="course-price">{c.effective_price === 0 ? "FREE" : fmtPrice(c.effective_price, currency)}</span>
                  {onSale && <><span className="old">{fmtPrice(c.price, currency)}</span><span className="disc">{disc}% off</span></>}
                </div>
                {onSale ? <SaleTimer ends={c.sale_ends_at} /> : <div style={{ height: 8 }} />}
              </>
            )}
            {c.enrolled ? (
              <Link to={`/learn/${c.id}`} className="btn-primary btn-big btn-block" data-testid="go-to-course-btn">Go to Course →</Link>
            ) : c.effective_price === 0 ? (
              <button className="btn-primary btn-big btn-block" data-testid="enroll-free-btn" onClick={enrollFree}>Enroll Free</button>
            ) : (
              <>
                <button className="btn-primary btn-big btn-block" data-testid="course-add-cart-btn"
                  onClick={() => { if (inCart) nav("/cart"); else { toggleCart(c.id); toast.success("Added to cart ✓"); } }}>
                  {inCart ? "✓ In Cart — Go to Cart" : "Add to Cart"}
                </button>
                <button className="btn-outline btn-big btn-block" data-testid="course-buy-now-btn"
                  onClick={() => { if (!inCart) toggleCart(c.id); nav("/cart"); }}>Buy Now</button>
              </>
            )}
            {(c.bundles || []).map((b) => (
              <div className="cross-sell" key={b.id} data-testid={`cross-sell-${b.id}`}>
                <span className="cs-badge">🎁 PART OF A BUNDLE — SAVE {b.savings_pct}%</span>
                <p className="cs-title">{b.title}</p>
                <p className="cs-line">{b.courses.map((x) => x.title).join(" + ")}</p>
                <div className="cs-row">
                  <span className="cs-price">{fmtPrice(b.price, currency)} <s>{fmtPrice(b.total_value, currency)}</s></span>
                  <button className="btn-outline" style={{ padding: "6px 14px", fontSize: 12 }} data-testid={`cross-sell-add-${b.id}`}
                    onClick={() => {
                      if (!bundleCart.includes(b.id)) { toggleBundle(b.id); toast.success("Bundle added to cart 🎁"); }
                      nav("/cart");
                    }}>
                    {bundleCart.includes(b.id) ? "IN CART →" : "GET BUNDLE"}
                  </button>
                </div>
              </div>
            ))}
            <div className="guarantee">14-Day Money-Back Guarantee · Full Lifetime Access</div>
            <div className="secure-pay-sm" data-testid="secure-pay-course">🔒 Secure payment via <b>Stripe</b> &amp; <b>PayPal</b></div>
            <div className="includes"><b>THIS COURSE INCLUDES</b>
              <div>🎬 {len.h}h {len.m}m on-demand video</div>
              {c.includes_assets && <div data-testid="course-includes-assets">📁 {c.includes_assets_text || "Complete project files & assets"}</div>}
              <div>📱 Access on mobile and desktop</div>
              <div>🏆 Certificate of completion</div>
            </div>
          </div>
        </div></aside>
      </div></div>

      {modal && (
        <div className="modal-bg" data-testid="preview-modal" onClick={(e) => { if (e.target.classList.contains("modal-bg")) setModal(null); }}>
          <button className="modal-close" data-testid="modal-close" onClick={() => setModal(null)}>✕</button>
          <div className="modal">
            <div className="modal-video">
              {modal.url ? (modal.url.includes("youtube") ?
                <iframe src={modal.url} title={modal.title} allow="autoplay;encrypted-media;picture-in-picture" allowFullScreen loading="lazy" />
                : <video src={mediaUrl(modal.url)} poster={modal.url.includes("/api/media/") ? mediaUrl(modal.url.split("?")[0] + "/thumb") : undefined}
                    controls autoPlay controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} data-testid="preview-video" />)
                : <div className="no-vid">◈ PREVIEW COMING SOON</div>}
            </div>
            <div className="modal-body"><h2>{modal.title}</h2><p style={{ color: "var(--muted)", fontSize: 13.5 }}>{c.title}</p></div>
          </div>
        </div>
      )}
    </div>
  );
}
